import { calculateDocumentTotal } from './calculations';
import type { Discount } from './calculations';
import type { WorkOrderLineItem } from '../types';
import type { PricingData } from '../components/WorkOrder/WorkOrderFinancials';

// Local copy — calculations.ts intentionally does not export round2.
const round2 = (v: number) => Math.round(v * 100) / 100;

/**
 * Authoritative work-order pricing for the frontend.
 *
 * The authoritative numbers (lineItemsTotal, subtotal, taxAmount, total, balanceDue)
 * mirror the backend `recalculateWorkOrderTotals` exactly: subtotal is the sum of
 * APPROVED line_total values, then `calculateDocumentTotal` applies whole-job
 * discount + tax. The labor/parts breakdown is informational only and never drives
 * the total. Part discounts are line-level (NOT multiplied by quantity).
 */
export function computeWorkOrderTotals(
  lineItems: WorkOrderLineItem[] | undefined | null,
  taxRate: number,
  wholeJobDiscount?: Discount,
  amountPaid: number = 0
): PricingData {
  const approved = (lineItems || []).filter(
    (i) => i.customer_approval_status === 'approved'
  );

  // Authoritative subtotal — identical to backend (sum of stored approved line_total).
  const lineItemsTotal = round2(
    approved.reduce((sum, i) => sum + (i.line_total || 0), 0)
  );

  // Whole-job discount + tax via the shared primitive (same code path as backend).
  const doc = calculateDocumentTotal(lineItemsTotal, taxRate, wholeJobDiscount);

  // Informational breakdown only (display rows); not the authoritative total. Minor float variance possible.
  let laborSubtotal = 0, laborDiscounts = 0, partsSubtotal = 0, partsDiscounts = 0, partsCost = 0;
  for (const item of approved) {
    laborSubtotal += round2((item.labor_hours || 0) * (item.labor_rate || 0));
    laborDiscounts += item.labor_discount_amount || 0;
    for (const part of item.parts || []) {
      partsSubtotal += round2((part.unit_price || 0) * (part.quantity || 0));
      partsDiscounts += part.discount_amount || 0; // line-level — do NOT multiply by quantity
      // Cost basis: prefer the authoritative owner cost (cost_cents, integer cents)
      // when present, else fall back to the legacy `cost` dollars field. Without the
      // cost_cents branch, an owner-entered part cost never reaches parts cost/margin,
      // so the Shop Margins card stayed stale after a save.
      const unitCost = part.cost_cents != null ? part.cost_cents / 100 : (part.cost || 0);
      partsCost += unitCost * (part.quantity || 0);
    }
  }
  laborSubtotal = round2(laborSubtotal);
  laborDiscounts = round2(laborDiscounts);
  partsSubtotal = round2(partsSubtotal);
  partsDiscounts = round2(partsDiscounts);
  partsCost = round2(partsCost);

  const subtotal = round2(lineItemsTotal - doc.discountAmount); // taxable amount
  const partsRevenue = round2(partsSubtotal - partsDiscounts);

  return {
    laborSubtotal, laborDiscounts, partsSubtotal, partsDiscounts,
    lineItemsTotal, jobDiscountAmount: doc.discountAmount,
    subtotal, taxRate, taxAmount: doc.taxAmount, total: doc.total,
    paid: amountPaid, balanceDue: round2(doc.total - amountPaid),
    partsCost, partsRevenue, partsMargin: round2(partsRevenue - partsCost),
    approvedItems: approved,
  };
}
